// Author profile page - The Borgesian Author Biography
// Presents Carlos Chopra as a real person while echoing unreliable narration.
// Features: terminal dossier aesthetic, redacted hover-reveal text,
// data corruption effects, glitched portrait, text mutation when out of view.
// Bio facts randomly mutate to different book variants over time.

import { glitch, setupTextMutation } from '../scripts/effects';
import { appendReturnToHouse } from '../scripts/navigation';
import authorPortrait from '../assets/images/author-portrait.svg';
import authorBioData from '../content/author-bio.json';
import {
  getInitialAuthorFacts,
  setupAuthorFactMutation
} from '../scripts/content-randomizer';

// Type definitions for author bio JSON structure
interface BioItem {
  id: string;
  text: string;
  type: 'normal' | 'italic' | 'redacted' | 'glitch';
  revealText?: string;
}

interface DetailedSection {
  title: string;
  content: string[];
  redactedContent?: string;
  note?: string;
  warning?: string;
}

interface Quote {
  text: string;
  attribution: string;
}

interface MutationVariant {
  id: string;
  originalText: string;
  mutatedText: string;
}

// Track cleanup functions
let cleanupFunctions: (() => void)[] = [];

/**
 * Render the author profile page
 * Terminal-style dossier with redacted sections and interactive elements
 */
export function renderAuthor(): void {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = '';
  cleanupFunctions = [];

  const container = document.createElement('div');
  container.className = 'author-container';

  // Get randomized initial author facts
  const authorFacts = getInitialAuthorFacts();

  // Build the page structure
  container.innerHTML = `
    <!--
      ═══════════════════════════════════════════════════════════════════════
      DOSSIER: CARLOS CHOPRA
      CLASSIFICATION: UNCERTAIN
      ═══════════════════════════════════════════════════════════════════════

      Who is Carlos Chopra?

      The author. The character. The narrator. The manuscript itself.
      The question has no stable answer.

      If you seek hidden truths, return to the house and explore:
        - The BASEMENT (type "carlos" or "wesleyan")
        - The ATTIC (type "chopra" or click the ceiling corners)
        - The ARCHIVE (type "vectors" or "manuscript")

      Or invoke from the console:
        __chopras.discover('basement')
        __chopras.discover('attic')
        __chopras.discover('archive')

      "I do not trust narrators. Especially this one."
      ═══════════════════════════════════════════════════════════════════════
    -->
    <div class="author-inner">
      <!-- Terminal profile box -->
      <div class="author-profile">
        <header class="author-profile__header" id="main-content" tabindex="-1">
          <span class="author-profile__subject">${authorBioData.header.subject}</span>
          <span class="author-profile__status author-profile__status--uncertain">${authorBioData.header.status}</span>
        </header>

        <div class="author-profile__body">
          <!-- Portrait with glitch effect -->
          <figure class="author-portrait-container">
            <img
              src="${authorPortrait}"
              alt="Carlos Chopra - Subject photograph, authenticity uncertain"
              class="author-portrait"
              tabindex="0"
              role="button"
              aria-label="Click to glitch the portrait"
            />
            <figcaption class="author-portrait-caption author-portrait-caption--uncertain">
              authenticity unverified
            </figcaption>
          </figure>

          <!-- Main biography content -->
          <article class="author-bio" aria-label="Author biography">
            ${renderBioItems(authorFacts as BioItem[])}
          </article>
        </div>
      </div>

      <!-- Detailed sections -->
      <div class="author-sections">
        ${renderDetailedSections(authorBioData.detailedSections as Record<string, DetailedSection>)}
      </div>

      <!-- Quotes carousel/list -->
      <div class="author-quotes">
        ${renderQuotes(authorBioData.quotes as Quote[])}
      </div>

      <!-- Document metadata (mysterious) -->
      <footer class="author-footer">
        <div class="author-timestamp author-timestamp--corrupted">
          ${authorBioData.metadata.lastVerified}
        </div>
        <div class="author-meta">
          <div class="author-meta-item">
            <span class="author-meta-label">Document Version</span>
            <span class="author-meta-value author-meta-value--corrupted">${authorBioData.metadata.documentVersion}</span>
          </div>
          <div class="author-meta-item">
            <span class="author-meta-label">Reliability</span>
            <span class="author-meta-value author-meta-value--corrupted">${authorBioData.metadata.reliability}</span>
          </div>
        </div>
        <p class="author-warning">${authorBioData.metadata.sourceNotes}</p>
      </footer>
    </div>
  `;

  app.appendChild(container);

  // Add return to house navigation
  const inner = container.querySelector('.author-inner');
  if (inner) {
    appendReturnToHouse(inner as HTMLElement, { className: 'author-nav' });
  }

  // Setup interactive elements
  setupRedactedText(container);
  setupPortraitGlitch(container);
  setupTextMutations(container, authorBioData.mutationVariants as MutationVariant[]);
  setupDetailedSectionReveals(container);
  setupLiveMutations(container);
}

/**
 * Render biography items from JSON data
 */
function renderBioItems(items: BioItem[]): string {
  return items.map(item => {
    switch (item.type) {
      case 'italic':
        return `<p class="author-bio-item" data-fact-id="${item.id}" data-fact-type="italic"><em class="author-bio-text">${escapeHtml(item.text)}</em></p>`;

      case 'redacted':
        return `
          <p class="author-redacted-section author-bio-item"
             data-fact-id="${item.id}"
             data-fact-type="redacted"
             data-reveal="${escapeHtml(item.revealText || '')}"
             tabindex="0"
             role="button"
             aria-label="Redacted text - hover or focus to reveal">
            <span class="author-redacted-content author-bio-text">${escapeHtml(item.text)}</span>
          </p>`;

      case 'glitch':
        return `<p class="author-corrupted--static author-bio-item" data-fact-id="${item.id}" data-fact-type="glitch" data-scramble="${generateScramble(item.text)}"><span class="author-bio-text">${escapeHtml(item.text)}</span></p>`;

      default:
        // Check for inline redactions (█ blocks)
        const processed = processInlineRedactions(item.text);
        return `<p class="author-bio-item" data-fact-id="${item.id}" data-fact-type="normal" data-mutable-id="${item.id}"><span class="author-bio-text">${processed}</span></p>`;
    }
  }).join('\n');
}

/**
 * Setup live content mutations for author facts
 * Facts will randomly change to different book variants over time
 */
function setupLiveMutations(container: HTMLElement): void {
  const items = container.querySelectorAll('.author-bio-item');

  items.forEach((item) => {
    const el = item as HTMLElement;
    const factId = el.dataset.factId;
    const factType = el.dataset.factType;

    if (!factId) return;

    const cleanup = setupAuthorFactMutation(el, factId, (newFact: BioItem) => {
      const textEl = el.querySelector('.author-bio-text');
      if (!textEl) return;

      // Update content based on type
      if (factType === 'redacted' || newFact.type === 'redacted') {
        // For redacted items, update both the visible text and reveal text
        textEl.textContent = newFact.text;
        if (newFact.revealText) {
          el.dataset.reveal = newFact.revealText;
        }
      } else if (factType === 'glitch' || newFact.type === 'glitch') {
        textEl.textContent = newFact.text;
        el.dataset.scramble = generateScramble(newFact.text);
      } else {
        // Normal or italic - just update text (handle inline redactions)
        textEl.innerHTML = processInlineRedactions(newFact.text);
      }

      // Update the fact ID for next mutation
      el.dataset.factId = newFact.id;
    });

    cleanupFunctions.push(cleanup);
  });
}

/**
 * Process text for inline redacted blocks (████)
 */
function processInlineRedactions(text: string): string {
  // Match sequences of █ characters
  return escapeHtml(text).replace(/█+/g, (match) => {
    return `<span class="author-redacted--block">${match}</span>`;
  });
}

/**
 * Render detailed sections (Early Life, Wesleyan, etc.)
 */
function renderDetailedSections(sections: Record<string, DetailedSection>): string {
  return Object.entries(sections).map(([key, section]) => `
    <section class="author-bio__section" data-section="${key}">
      <h2 class="author-bio__label">${section.title}</h2>
      ${section.content.map(p => {
        const processed = processInlineRedactions(p);
        return `<p>${processed}</p>`;
      }).join('\n')}
      ${section.redactedContent ? `
        <p class="author-redacted-section"
           data-reveal="${escapeHtml(section.redactedContent)}"
           tabindex="0"
           role="button"
           aria-label="Redacted section - hover or focus to reveal">
          <span class="author-redacted-content">[CLASSIFIED]</span>
        </p>
      ` : ''}
      ${section.note ? `
        <p class="author-corrupted"><em>${escapeHtml(section.note)}</em></p>
      ` : ''}
      ${section.warning ? `
        <p class="author-warning">${escapeHtml(section.warning)}</p>
      ` : ''}
    </section>
  `).join('\n');
}

/**
 * Render author quotes
 */
function renderQuotes(quotes: Quote[]): string {
  return quotes.map((quote, index) => `
    <blockquote class="author-quote${index === 0 ? ' author-quote--glow' : ''}">
      "${escapeHtml(quote.text)}"
      <cite>${escapeHtml(quote.attribution)}</cite>
    </blockquote>
  `).join('\n');
}

/**
 * Generate scrambled text for glitch effect
 */
function generateScramble(text: string): string {
  const glitchChars = '█▓▒░╔╗╚╝║═┌┐└┘├┤┬┴┼';
  return text.split('').map(char => {
    if (char === ' ') return ' ';
    if (Math.random() < 0.3) {
      return glitchChars[Math.floor(Math.random() * glitchChars.length)];
    }
    return char;
  }).join('');
}

/**
 * Setup all redacted text hover/focus reveals
 */
function setupRedactedText(container: HTMLElement): void {
  const redactedElements = container.querySelectorAll('.author-redacted-section');

  redactedElements.forEach(element => {
    const el = element as HTMLElement;
    const contentSpan = el.querySelector('.author-redacted-content');

    // Store original redacted text pattern
    const originalContent = contentSpan?.textContent || '[REDACTED]';

    // Track state
    let isRevealed = false;

    const reveal = () => {
      const revealText = el.getAttribute('data-reveal') || '';
      if (!isRevealed && revealText) {
        isRevealed = true;
        el.classList.add('author-redacted-section--revealed');
        if (contentSpan) {
          contentSpan.textContent = revealText;
        }
      }
    };

    const hide = () => {
      if (isRevealed) {
        isRevealed = false;
        el.classList.remove('author-redacted-section--revealed');
        if (contentSpan) {
          // Restore to current redacted pattern (may have been mutated)
          const currentText = el.querySelector('.author-bio-text')?.textContent;
          contentSpan.textContent = currentText?.includes('█') ? currentText : originalContent;
        }
      }
    };

    // Mouse events
    el.addEventListener('mouseenter', reveal);
    el.addEventListener('mouseleave', hide);

    // Keyboard accessibility
    el.addEventListener('focus', reveal);
    el.addEventListener('blur', hide);

    // Touch support
    el.addEventListener('touchstart', (e) => {
      e.preventDefault();
      if (isRevealed) {
        hide();
      } else {
        reveal();
      }
    }, { passive: false });
  });
}

/**
 * Setup portrait glitch effect on click
 */
function setupPortraitGlitch(container: HTMLElement): void {
  const portrait = container.querySelector('.author-portrait') as HTMLElement;
  if (!portrait) return;

  let isGlitching = false;

  const triggerGlitch = () => {
    if (isGlitching) return;

    isGlitching = true;
    portrait.classList.add('glitching');
    glitch(portrait);

    setTimeout(() => {
      portrait.classList.remove('glitching');
      isGlitching = false;
    }, 500);
  };

  portrait.addEventListener('click', triggerGlitch);
  portrait.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      triggerGlitch();
    }
  });
}

/**
 * Setup text mutation effect - text changes when scrolled out of view
 * Matches elements by their data-mutable-id to mutation variants
 */
function setupTextMutations(container: HTMLElement, variants: MutationVariant[]): void {
  // Build a map of original text -> mutation variants for faster lookup
  const variantMap = new Map<string, string[]>();
  variants.forEach((v) => {
    const existing = variantMap.get(v.originalText) || [];
    existing.push(v.mutatedText);
    variantMap.set(v.originalText, existing);
  });

  // Find elements that can mutate
  const mutableElements = container.querySelectorAll('[data-mutable-id]');

  mutableElements.forEach((element) => {
    const el = element as HTMLElement;
    const elementText = el.textContent?.trim() || '';

    // Look for variants where the original text is contained in this element
    // This handles cases where element might have extra text or formatting
    let matchedOriginal: string | null = null;
    let matchedVariants: string[] = [];

    for (const [originalText, mutatedTexts] of variantMap.entries()) {
      // Check if this element contains the original text
      // We normalize whitespace for comparison
      const normalizedElement = elementText.replace(/\s+/g, ' ');
      const normalizedOriginal = originalText.replace(/\s+/g, ' ');

      if (normalizedElement.includes(normalizedOriginal)) {
        // Found a match - collect all variants for this original
        matchedOriginal = originalText;
        matchedVariants = mutatedTexts;
        break; // Use first match
      }
    }

    if (matchedOriginal && matchedVariants.length > 0) {
      el.classList.add('author-mutable');

      // Setup intersection observer for mutation
      // Store the full element text as original, and provide mutated versions
      // that replace the matched portion
      const fullMutatedTexts = matchedVariants.map((mutatedText) => {
        // Replace the original text portion with the mutated version
        return elementText.replace(matchedOriginal!, mutatedText);
      });

      const cleanup = setupTextMutation(el, elementText, fullMutatedTexts);
      cleanupFunctions.push(cleanup);
    }
  });
}

/**
 * Setup detailed section reveals (expand on click)
 */
function setupDetailedSectionReveals(container: HTMLElement): void {
  const sections = container.querySelectorAll('.author-bio__section');

  sections.forEach(section => {
    const label = section.querySelector('.author-bio__label');
    if (label) {
      // Add collapsed indicator
      section.classList.add('author-bio__section--collapsible');

      // Click to expand (optional - sections are visible by default)
      label.setAttribute('role', 'button');
      label.setAttribute('tabindex', '0');
      label.setAttribute('aria-expanded', 'true');
    }
  });
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Cleanup author page - remove event listeners, observers, etc.
 */
export function cleanupAuthor(): void {
  cleanupFunctions.forEach(fn => fn());
  cleanupFunctions = [];
}
